import React, { useEffect, useState } from 'react';

import { LoadingOutlined } from '@ant-design/icons';
import { Alert, DatePicker, Empty, Flex, Pagination, Table } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';

import { useAppSelector, useStyle } from 'segments/hooks';

import { PagintaionParams } from 'shared/CustomPagination/CustomPagination';

import { convertCoachesBookingsToTable, CoachesBooking, SelectedBookingCoach, useGetCoachesBookingQuery } from 'features/coachesBooking';
import { selectWorkTime } from 'features/config';
import { selectAccountData } from 'features/user';

import BookingTableColumns from './BookingTableColumns/BookingTableColumns';


const todayDate = dayjs();
interface Props {
  handleBooking: (arg: SelectedBookingCoach) => void;
  handleCheckDetail: (id: number) => void;
}

type CoachBooking = CoachesBooking[number];
export interface TableData extends CoachBooking {
  key: number;
}

export function CoachesBookings({ handleBooking, handleCheckDetail }: Props) {
  const { t } = useTranslation();
  const [bookingTime, setBookingTime] = useState(todayDate);
  const [params, setParams] = useState<PagintaionParams>();
  const { styles } = useStyle();
  const { data = [], isLoading, isError } = useGetCoachesBookingQuery({
    dateFrom: bookingTime.format('YYYY-MM-DD'),
    dateTo: bookingTime.format('YYYY-MM-DD')
  });
  const columnCount = data.length === 0 ? null : data[0].columns.length;
  const workTime = useAppSelector(selectWorkTime);
  const userInfo = useAppSelector(selectAccountData);

  useEffect(() => {
    if (columnCount !== null) {
      setParams({
        itemsCount: columnCount,
        pageSize: columnCount,
        pageNumber: 1,
      });
    }
  }, [setParams, columnCount]);

  const handleTimeChange = (time: Dayjs | null) => setBookingTime(time ?? todayDate);

  if (isLoading) {
    return <LoadingOutlined />;
  }

  if (isError || data.length === 0 || columnCount === 0) {
    return (
      <>
        <DatePicker value={bookingTime} onChange={handleTimeChange} />
        {isError ? <Alert type="error" showIcon message={t('admin-extensions.load-error')} /> : <Empty />}
      </>
    );
  }

  if (params?.itemsCount !== columnCount || params === undefined) {
    return <LoadingOutlined />;
  }

  const columns = BookingTableColumns({
    items: data,
    handleBooking,
    handleCheckDetail,
    onTimeChange: handleTimeChange,
    bookingTime,
    params,
    workTime,
    userInfo
  });

  const handleChange = (page: number, size: number) => {
    setParams({
      ...params,
      pageNumber: page,
      pageSize: size,
    });
  };

  return (
    <Table<TableData>
      className={styles.customTable}
      size="small"
      dataSource={convertCoachesBookingsToTable(data)}
      loading={isLoading}
      scroll={{ x: 1200, y: '80vh' }}
      bordered
      pagination={false}
      columns={columns}
      footer={() => (
        <Flex justify='center'>
          <Pagination total={params.itemsCount} pageSize={params.pageSize} pageSizeOptions={[5, 10, 15, 20, columnCount]} onChange={handleChange} showSizeChanger />
        </Flex>
      )}
    />
  );
}
